import React, { useEffect, useRef, useState } from 'react'
import { User, Target, Briefcase, BookOpen } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { CrmEntityType } from '../types'

export type MentionContact = {
  id: string
  name: string
  email: string
  company: string
  type?: CrmEntityType
}

export type MentionEntity = {
  id: string
  type: CrmEntityType
  title: string
  subtitle?: string
  metadata?: Record<string, unknown>
}

interface MentionPopoverProps {
  contacts?: MentionContact[]
  entities?: MentionEntity[]
  loading: boolean
  onSelect: (entity: MentionEntity) => void
  onDismiss: () => void
  activeIndex: number
  selectedCategory?: 'all' | CrmEntityType
  onCategoryChange?: (cat: 'all' | CrmEntityType) => void
}

export const MentionPopover: React.FC<MentionPopoverProps> = ({
  contacts = [],
  entities,
  loading,
  onSelect,
  onDismiss,
  activeIndex,
  selectedCategory,
  onCategoryChange,
}) => {
  const { currentTheme } = useTheme()
  const ref = useRef<HTMLDivElement>(null)
  const [internalCategory, setInternalCategory] = useState<'all' | CrmEntityType>('all')

  const activeCategory = selectedCategory ?? internalCategory
  const handleSetCategory = (cat: 'all' | CrmEntityType) => {
    if (onCategoryChange) {
      onCategoryChange(cat)
    } else {
      setInternalCategory(cat)
    }
  }

  // Normalise list of items into unified MentionEntity format
  const itemList: MentionEntity[] = React.useMemo(() => {
    if (entities && entities.length > 0) return entities

    if (contacts && contacts.length > 0) {
      return contacts.map((c) => ({
        id: c.id,
        type: c.type || 'contact',
        title: c.name,
        subtitle: [c.company, c.email].filter(Boolean).join(' · '),
        metadata: { id: c.id, name: c.name, email: c.email, company: c.company },
      }))
    }

    return []
  }, [contacts, entities])

  const categoryCounts = React.useMemo(() => {
    const counts = { all: itemList.length, lead: 0, contact: 0, deal: 0, kb: 0 }
    for (const item of itemList) {
      if (item.type in counts) {
        counts[item.type]++
      }
    }
    return counts
  }, [itemList])

  const filteredItems = React.useMemo(() => {
    if (activeCategory === 'all') return itemList
    return itemList.filter((item) => item.type === activeCategory)
  }, [itemList, activeCategory])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onDismiss()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onDismiss])

  useEffect(() => {
    const el = ref.current?.querySelector(`[data-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const getEntityIcon = (type: CrmEntityType) => {
    switch (type) {
      case 'lead':
        return <Target className="w-4 h-4 text-amber-400" />
      case 'deal':
        return <Briefcase className="w-4 h-4 text-emerald-400" />
      case 'kb':
        return <BookOpen className="w-4 h-4 text-sky-400" />
      case 'contact':
      default:
        return <User className="w-4 h-4 text-indigo-400" />
    }
  }

  const getTypeBadgeColor = (type: CrmEntityType) => {
    switch (type) {
      case 'lead':
        return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
      case 'deal':
        return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
      case 'kb':
        return 'bg-sky-500/15 text-sky-400 border-sky-500/30'
      case 'contact':
      default:
        return 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30'
    }
  }

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 right-0 mb-2 mx-4 max-w-[60rem] rounded-xl overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-150 z-30"
      style={{
        backgroundColor: currentTheme.secondary,
        border: `1px solid ${currentTheme.border}`,
        boxShadow: currentTheme.cardShadow,
      }}
    >
      {/* Header & Category Tabs */}
      <div
        className="px-3 py-2 border-b flex flex-wrap items-center justify-between gap-2"
        style={{ borderColor: currentTheme.border }}
      >
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            CRM Entity Mentions
          </p>
          <div className="flex items-center gap-1">
            {(['all', 'lead', 'contact', 'deal', 'kb'] as const).map((cat) => {
              const count = categoryCounts[cat]
              const isSelected = activeCategory === cat
              return (
                <button
                  key={cat}
                  onClick={() => handleSetCategory(cat)}
                  className={`text-[11px] px-2 py-0.5 rounded font-medium capitalize transition-all flex items-center gap-1 ${
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'hover:bg-slate-500/15 text-slate-400'
                  }`}
                >
                  <span>{cat === 'kb' ? 'KB Articles' : cat}</span>
                  <span className={`text-[10px] opacity-75 font-mono`}>({count})</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono">
          <span>↑↓ Navigate</span>
          <span>↵ Embed Token</span>
          <span>Esc Close</span>
        </div>
      </div>

      {/* Entity Item List */}
      <div className="max-h-72 overflow-y-auto custom-scrollbar py-1">
        {loading && (
          <div className="px-4 py-6 text-center">
            <div className="inline-block w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-2" />
            <p className="text-xs text-slate-400">Searching CRM entities (/leads, /contacts, /deals, /kb)...</p>
          </div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div className="px-4 py-6 text-center">
            <p className="text-xs text-slate-400">No CRM entities found matching query</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Try searching by name, company, email, or deal title
            </p>
          </div>
        )}

        {!loading &&
          filteredItems.map((item, i) => {
            const isActive = i === activeIndex
            return (
              <button
                key={`${item.type}-${item.id}`}
                data-index={i}
                onClick={() => onSelect(item)}
                className="flex items-center gap-3 w-full px-3 py-2.5 text-left transition-colors duration-100 group"
                style={{
                  backgroundColor: isActive ? currentTheme.hoverBackground : 'transparent',
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                  style={{
                    backgroundColor: isActive ? `${currentTheme.accent}33` : `${currentTheme.accent}1A`,
                  }}
                >
                  {getEntityIcon(item.type)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold truncate" style={{ color: currentTheme.text }}>
                      {item.title}
                    </p>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded font-mono uppercase tracking-wider border shrink-0 ${getTypeBadgeColor(
                        item.type
                      )}`}
                    >
                      @{item.type}
                    </span>
                  </div>
                  {item.subtitle && (
                    <p className="text-xs truncate mt-0.5" style={{ color: currentTheme.mutedText }}>
                      {item.subtitle}
                    </p>
                  )}
                </div>
              </button>
            )
          })}
      </div>
    </div>
  )
}
